import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, ChevronDown, ChevronUp, Package, Truck, CheckCircle2, Circle, Phone, Mail, MapPin, Copy } from "lucide-react";

const TOKEN_KEY = "lf_admin_token";

/* -------------------------------- Helpers -------------------------------- */
const fmt$ = (cents) => `$${(cents / 100).toFixed(2)}`;
const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso || "";
  }
};
const fulfilLabel = { PICKUP: "Pickup", DELIVERY: "Delivery", SHIPPING: "Shipping" };
const statusColor = {
  PAID: { bg: "#fef3c7", fg: "#78350f", label: "New · Paid" },
  PREPARING: { bg: "#dbeafe", fg: "#1e3a8a", label: "Preparing" },
  READY: { bg: "#d1fae5", fg: "#064e3b", label: "Ready" },
  OUT_FOR_DELIVERY: { bg: "#e0e7ff", fg: "#3730a3", label: "Out for delivery" },
  COMPLETED: { bg: "#e5e7eb", fg: "#374151", label: "Completed" },
  CANCELLED: { bg: "#fee2e2", fg: "#7f1d1d", label: "Cancelled" },
  PENDING_PAYMENT_CONFIG: { bg: "#fef2f2", fg: "#991b1b", label: "Payment pending" },
};

/* ---------------------------- Auth helper hook --------------------------- */
const authHeader = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/* ================================ Login ================================ */
const AdminLogin = ({ onLoggedIn }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back to La Fleur");
      onLoggedIn();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "var(--lf-cream)" }}
      data-testid="admin-login"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md p-10"
        style={{ background: "white", borderRadius: 28, boxShadow: "0 20px 60px -20px rgba(58,46,42,0.18)" }}
      >
        <div className="text-center mb-8">
          <div className="font-cursive text-5xl leading-none" style={{ color: "var(--lf-gold-bright)" }}>La Fleur</div>
          <div className="lf-eyebrow mt-3">Admin dashboard</div>
        </div>
        <label className="block text-sm mb-2" style={{ color: "var(--lf-ink-soft)" }}>Password</label>
        <div className="relative">
          <Lock size={16} style={{ position: "absolute", top: 14, left: 14, color: "var(--lf-ink-soft)" }} />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter admin password"
            required
            autoFocus
            className="w-full py-3 pl-10 pr-3 focus:outline-none"
            style={{ background: "var(--lf-cream)", border: "1px solid #ecdcd0", borderRadius: 12, fontFamily: "Outfit,sans-serif" }}
            data-testid="admin-password-input"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="lf-btn lf-btn-gold w-full mt-6 justify-center"
          data-testid="admin-login-submit"
        >
          {loading ? "Signing in..." : "Sign in"}
        </button>
        <p className="mt-6 text-xs text-center" style={{ color: "var(--lf-ink-soft)" }}>
          Session lasts 7 days · Private access only
        </p>
      </form>
    </div>
  );
};

/* ================================ Row ================================ */
const OrderRow = ({ order, onStatusChange }) => {
  const [expanded, setExpanded] = useState(false);
  const s = statusColor[order.status] || { bg: "#f4f4f5", fg: "#374151", label: order.status };

  const copyAddress = () => {
    const a = order.delivery_address;
    if (!a) return;
    const line = typeof a === "string"
      ? a
      : [a.line1, a.line2, `${a.city}, ${a.state} ${a.zip}`].filter(Boolean).join(", ");
    navigator.clipboard.writeText(line);
    toast.success("Address copied");
  };

  return (
    <div style={{ background: "white", borderRadius: 16, marginBottom: 12, border: "1px solid #f0e6df", overflow: "hidden" }}>
      <button
        onClick={() => setExpanded((x) => !x)}
        className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-neutral-50 transition"
        data-testid={`order-row-${order.id.slice(0, 8)}`}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium"
            style={{ background: s.bg, color: s.fg, borderRadius: 999, whiteSpace: "nowrap" }}
          >
            <Circle size={7} fill="currentColor" strokeWidth={0} />
            {s.label}
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-serif text-base truncate" style={{ color: "var(--lf-ink)" }}>
              {order.customer_name || "—"} · <span style={{ color: "var(--lf-ink-soft)" }}>{fulfilLabel[order.fulfillment_type] || order.fulfillment_type}</span>
            </div>
            <div className="text-xs mt-0.5" style={{ color: "var(--lf-ink-soft)" }}>
              #{order.id.slice(0, 8)} · {fmtDate(order.created_at)} · {order.items.length} item{order.items.length !== 1 ? "s" : ""}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 pl-3">
          <div className="font-serif text-lg" style={{ color: "var(--lf-gold-deep)" }}>{fmt$(order.total)}</div>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {expanded && (
        <div style={{ borderTop: "1px solid #f0e6df", background: "var(--lf-cream)" }} className="px-5 py-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact + Fulfillment */}
            <div>
              <div className="lf-eyebrow mb-3">Customer</div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Phone size={14} style={{ color: "var(--lf-ink-soft)" }} />
                  <a href={`tel:${order.customer_phone}`} className="underline" style={{ color: "var(--lf-gold-deep)" }}>
                    {order.customer_phone || "—"}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={14} style={{ color: "var(--lf-ink-soft)" }} />
                  <a href={`mailto:${order.customer_email}`} className="underline" style={{ color: "var(--lf-gold-deep)" }}>
                    {order.customer_email || "—"}
                  </a>
                </div>
                {order.delivery_address && (
                  <div className="flex items-start gap-2 pt-1">
                    <MapPin size={14} style={{ color: "var(--lf-ink-soft)", marginTop: 3 }} />
                    <div className="flex-1">
                      {typeof order.delivery_address === "string" ? (
                        <div>{order.delivery_address}</div>
                      ) : (
                        <>
                          <div>{order.delivery_address.line1} {order.delivery_address.line2}</div>
                          <div>{order.delivery_address.city}, {order.delivery_address.state} {order.delivery_address.zip}</div>
                        </>
                      )}
                    </div>
                    <button onClick={copyAddress} title="Copy address" className="p-1" data-testid={`copy-address-${order.id.slice(0,8)}`}>
                      <Copy size={14} style={{ color: "var(--lf-gold-deep)" }} />
                    </button>
                  </div>
                )}
                {order.delivery_date && (
                  <div className="text-xs pt-2" style={{ color: "var(--lf-ink-soft)" }}>
                    Requested: <strong style={{ color: "var(--lf-ink)" }}>{order.delivery_date}</strong>
                  </div>
                )}
              </div>

              {order.card_message && (
                <div className="mt-4 p-3" style={{ background: "white", borderLeft: "3px solid var(--lf-pink-deep)", borderRadius: 8 }}>
                  <div className="lf-eyebrow" style={{ fontSize: "0.6rem" }}>Card message</div>
                  <div className="font-serif italic mt-1 text-sm" style={{ color: "var(--lf-ink)" }}>&ldquo;{order.card_message}&rdquo;</div>
                </div>
              )}
            </div>

            {/* Items */}
            <div>
              <div className="lf-eyebrow mb-3">Items</div>
              <div className="space-y-2 text-sm">
                {order.items.map((it, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 py-2" style={{ borderBottom: "1px solid #f0e6df" }}>
                    <div className="flex-1">
                      <div style={{ color: "var(--lf-ink)" }}>
                        <strong>{it.quantity}×</strong> {it.name}
                      </div>
                      {it.variant_id && (
                        <div className="text-xs" style={{ color: "var(--lf-ink-soft)" }}>{it.variant_id}</div>
                      )}
                      {it.notes && (
                        <div className="text-xs italic mt-1" style={{ color: "var(--lf-pink-deep)" }}>Notes: {it.notes}</div>
                      )}
                    </div>
                    <div style={{ color: "var(--lf-ink)" }}>{fmt$(it.line_total)}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-3 space-y-1 text-sm" style={{ borderTop: "2px solid #ecdcd0" }}>
                <div className="flex justify-between"><span style={{ color: "var(--lf-ink-soft)" }}>Subtotal</span><span>{fmt$(order.subtotal)}</span></div>
                <div className="flex justify-between"><span style={{ color: "var(--lf-ink-soft)" }}>{fulfilLabel[order.fulfillment_type] || order.fulfillment_type} fee</span><span>{fmt$(order.delivery_fee)}</span></div>
                <div className="flex justify-between font-serif text-lg pt-2"><strong>Total</strong><strong style={{ color: "var(--lf-gold-deep)" }}>{fmt$(order.total)}</strong></div>
              </div>
            </div>
          </div>

          {/* Status change */}
          <div className="mt-5 pt-5 flex flex-wrap items-center gap-2" style={{ borderTop: "1px solid #f0e6df" }}>
            <span className="lf-eyebrow mr-2">Update status:</span>
            {["PAID", "PREPARING", "READY", "OUT_FOR_DELIVERY", "COMPLETED"].map((st) => (
              <button
                key={st}
                onClick={() => onStatusChange(order.id, st)}
                disabled={order.status === st}
                className="px-3 py-1.5 text-xs rounded-full transition"
                style={{
                  background: order.status === st ? "var(--lf-gold-deep)" : "white",
                  color: order.status === st ? "white" : "var(--lf-ink)",
                  border: "1px solid " + (order.status === st ? "var(--lf-gold-deep)" : "#e5d8ce"),
                  cursor: order.status === st ? "default" : "pointer",
                  opacity: order.status === st ? 1 : 0.9,
                }}
                data-testid={`status-btn-${st}-${order.id.slice(0,8)}`}
              >
                {statusColor[st]?.label || st}
              </button>
            ))}
          </div>

          <div className="mt-3 text-xs" style={{ color: "var(--lf-ink-soft)" }}>
            Order ID: {order.id} · Payment ID: {order.payment_id || "—"}
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================== Dashboard ============================== */
const AdminDashboard = ({ onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, s] = await Promise.all([
        api.get("/admin/orders", { headers: authHeader() }),
        api.get("/admin/summary", { headers: authHeader() }),
      ]);
      setOrders(o.data.orders);
      setSummary(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        onLogout();
      } else {
        toast.error("Failed to load orders");
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // auto-refresh every minute
    return () => clearInterval(t);
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status }, { headers: authHeader() });
      toast.success(`Marked as ${statusColor[status]?.label || status}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const filtered = orders.filter((o) => {
    if (filter === "ALL") return true;
    if (filter === "ACTIVE") return ["PAID", "PREPARING", "READY", "OUT_FOR_DELIVERY"].includes(o.status);
    return o.status === filter;
  });

  return (
    <div style={{ background: "var(--lf-cream)", minHeight: "100vh" }} data-testid="admin-dashboard">
      {/* Header */}
      <div style={{ background: "white", borderBottom: "1px solid #f0e6df" }} className="px-6 py-5">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-cursive text-4xl leading-none" style={{ color: "var(--lf-gold-bright)" }}>La Fleur</div>
            <div className="lf-eyebrow mt-1">Orders dashboard</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="lf-btn lf-btn-outline" data-testid="refresh-btn">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={() => navigate("/")} className="lf-btn lf-btn-outline">View site</button>
            <button
              onClick={() => { localStorage.removeItem(TOKEN_KEY); onLogout(); }}
              className="lf-btn lf-btn-outline"
              data-testid="logout-btn"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="max-w-6xl mx-auto px-6 pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Today's orders", value: summary.orders_today, sub: fmt$(summary.revenue_today_cents), icon: Package },
              { label: "Pending fulfillment", value: summary.pending_fulfillment, sub: "Preparing & new", icon: Truck },
              { label: "All-time orders", value: summary.orders_total_all_time, sub: fmt$(summary.revenue_all_time_cents), icon: CheckCircle2 },
              { label: "Session", value: "Live", sub: "Auto-refresh 60s", icon: RefreshCw },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.label} className="p-4" style={{ background: "white", borderRadius: 16, border: "1px solid #f0e6df" }}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-xs" style={{ color: "var(--lf-ink-soft)" }}>{c.label}</div>
                      <div className="font-serif text-3xl mt-1" style={{ color: "var(--lf-ink)" }}>{c.value}</div>
                      <div className="text-xs mt-1" style={{ color: "var(--lf-gold-deep)" }}>{c.sub}</div>
                    </div>
                    <Icon size={20} style={{ color: "var(--lf-gold)" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters + Orders */}
      <div className="max-w-6xl mx-auto px-6 pt-8 pb-16">
        <div className="flex items-center gap-2 mb-5 overflow-x-auto">
          {[
            ["ALL", "All"],
            ["ACTIVE", "Active"],
            ["PAID", "New"],
            ["PREPARING", "Preparing"],
            ["READY", "Ready"],
            ["OUT_FOR_DELIVERY", "Out for delivery"],
            ["COMPLETED", "Completed"],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className="px-4 py-1.5 text-sm rounded-full whitespace-nowrap"
              style={{
                background: filter === k ? "var(--lf-ink)" : "white",
                color: filter === k ? "white" : "var(--lf-ink)",
                border: "1px solid " + (filter === k ? "var(--lf-ink)" : "#e5d8ce"),
              }}
              data-testid={`filter-${k}`}
            >
              {l}
            </button>
          ))}
        </div>

        {loading && orders.length === 0 ? (
          <div className="py-24 text-center" style={{ color: "var(--lf-ink-soft)" }}>Loading orders…</div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center" style={{ color: "var(--lf-ink-soft)" }}>
            No orders match this filter.
          </div>
        ) : (
          filtered.map((o) => <OrderRow key={o.id} order={o} onStatusChange={updateStatus} />)
        )}
      </div>
    </div>
  );
};

/* ============================= Route wrapper ============================ */
const Admin = () => {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  if (!authed) return <AdminLogin onLoggedIn={() => setAuthed(true)} />;
  return <AdminDashboard onLogout={() => setAuthed(false)} />;
};

export default Admin;
